import assign from 'lodash/assign'
import { Map, List } from 'immutable'
import { getConfigDataServer } from '../config'
import { SET_CONFIG_DATA } from '../config/redux/actions'
import { SET_PAGE_READY } from '../pages/redux/actions'
import {
  GET_AUTH_LOGIN,
  GET_AUTH_LOGOUT,
  GET_AUTH_DATA_LOCAL,
  GET_AUTH_RENEW,
  getAuthRenew,
  getAuthDataLocal,
  setAuthProcessing,
  setAuthData,
  setAuthLogin,
  setAuthLoginError,
} from './redux/actions'
import { login, logout, renew, setAuthLocal, getAuthLocal, getAuthJwt, isLoggedIn } from '.'
import { LOGIN_ROLES_ALLOWED } from './roles'

/**
 * How often the auth renewal interval should run
 */
const RENEW_INTERVAL_SECONDS = 300 // set to 5 minutes

/**
 * Renew JWT before it expires by this many seconds before it expires
 */
const RENEW_SECONDS_BEFORE_EXPIRE = 1800 // set to 30 minutes

/**
 * The authentication renewal interval id
 */
let _authenticationRenewalInterval

async function initAuthRenew ({ dispatch }) {
  if (_authenticationRenewalInterval) {
    clearInterval(_authenticationRenewalInterval)
    _authenticationRenewalInterval = undefined
  }
  dispatch(getAuthRenew())
  // Start a renewal interval
  _authenticationRenewalInterval = setInterval(
    () => dispatch(getAuthRenew()),
    RENEW_INTERVAL_SECONDS * 1000,
  )
  // Set the window focus event to renew
  if (window) {
    window.addEventListener('focus', () => dispatch(getAuthRenew()))
  }
}

export function getAuthLoginHandler ({ after }) {
  return after(GET_AUTH_LOGIN, async ({ dispatch, state, action } = {}) => {
    dispatch(setAuthLoginError())
    dispatch(setAuthProcessing(true))
    try {
      const { config } = state
      const loginOptions = assign({}, action.payload, {
        server: getConfigDataServer(config),
      })
      const data = await login(loginOptions)
      const roleIds = data.get('roleIds', List())
      const accessAllowed = LOGIN_ROLES_ALLOWED.some((role) => roleIds.includes(role))
      if (data.get('success') && accessAllowed) {
        // Unique event for login
        dispatch(setAuthLogin({ data }))
        // Generically set auth data
        // will add pageEntitlements to the auth data
        dispatch(setAuthData({ data }))
        setAuthLocal(data)
      } else if (data.get('success') && !accessAllowed) {
        dispatch(setAuthLoginError('Your account is not authorized to access this application'))
      } else {
        const errorMsg = data.get('messages').join(' ')
        dispatch(setAuthLoginError(errorMsg))
      }
    } catch (err) {
      dispatch(setAuthLoginError(err.toString()))
    }
    dispatch(setAuthProcessing(false))
  })
}

export function getAuthLogoutHandler ({ after }) {
  return after(GET_AUTH_LOGOUT, async ({ dispatch, state } = {}) => {
    const { config, auth } = state
    dispatch(setAuthProcessing(true))
    await logout({
      auth,
      server: getConfigDataServer(config),
    })
    dispatch(setAuthProcessing(false))
    setAuthLocal()
    if (window) {
      window.location.reload()
    }
  })
}

export function getAuthLocalHandler ({ after }) {
  return after(GET_AUTH_DATA_LOCAL, async ({ dispatch } = {}) => {
    const localAuthData = getAuthLocal()
    if (localAuthData) {
      dispatch(setAuthData({ data: localAuthData, processing: false }))
      return
    }
    dispatch(setAuthData({ data: Map(), processing: false }))
  })
}

export function getAuthRenewHandler ({ after }) {
  return after(GET_AUTH_RENEW, async ({ dispatch, state } = {}) => {
    const { auth = Map(), config } = state
    // If we have a JWT optimistically renew before it expires
    // by setting now to the future time when checking login
    const shouldRenew = getAuthJwt(auth)
      ? !isLoggedIn(auth, Math.floor(Date.now() / 1000) + RENEW_SECONDS_BEFORE_EXPIRE)
      : false
    if (!shouldRenew) {
      return
    }
    try {
      dispatch(setAuthProcessing(true))
      const data = await renew({
        auth,
        server: getConfigDataServer(config),
      })
      if (data.get('success')) {
        dispatch(setAuthData({ data, processing: false }))
        setAuthLocal(data)
      } else {
        dispatch(setAuthProcessing(false))
      }
    } catch (err) {
      dispatch(setAuthProcessing(false))
    }
  })
}

/**
 * Handle setting page ready
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take after function
 */
export function setPageReadyHandler ({ after } = {}) {
  return after(SET_PAGE_READY, async ({ dispatch } = {}) => {
    await initAuthRenew({ dispatch })
  })
}

/**
 * Handle getting the config data
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take after function
 */
export function setConfigDataHandler ({ after } = {}) {
  return after(SET_CONFIG_DATA, async ({ dispatch } = {}) => {
    dispatch(getAuthDataLocal())
  })
}
